// Intelligent Text-To-Speech (TTS) Helper for AILinkB
// Detects actual language of the text to prevent speaking English with a Polish accent
// Automatically selects the highest quality neural/native voice available in the browser

export const detectSpeechLanguage = (text, fallbackLang = 'pl-PL') => {
  if (!text) return fallbackLang;
  const lower = String(text).toLowerCase();

  const plWords = ['jest', 'witam', 'cześć', 'bardzo', 'dla', 'oraz', 'czy', 'możemy', 'pomoc', 'firma', 'usługi', 'dziękuję', 'tutaj', 'proszę', 'szukasz', 'klienta', 'oferujemy', 'zapraszamy', 'mamy', 'może', 'jestem', 'nasz', 'nasza', 'nasze', 'wasz', 'dlaczego', 'gdzie'];
  const enWords = ['the', 'you', 'can', 'with', 'help', 'welcome', 'hello', 'looking', 'company', 'services', 'thank', 'have', 'there', 'please', 'what', 'this', 'that', 'about', 'our', 'your', 'offer', 'would', 'like', 'how', 'when', 'why', 'where', 'are', 'will', 'from', 'best', 'more'];
  const deWords = ['und', 'für', 'sie', 'eine', 'willkommen', 'hallo', 'unternehmen', 'dienstleistungen', 'danke', 'helfen', 'bitte', 'haben', 'sind', 'nach', 'suchen', 'unsere', 'ihre', 'wir', 'was', 'wie', 'warum', 'können'];
  const frWords = ['pour', 'avec', 'vous', 'nous', 'bonjour', 'entreprise', 'services', 'merci', 'aider', 'bienvenue', 'recherche', 'dans', 'comment', 'votre', 'notre', 'avoir', 'être'];
  const esWords = ['para', 'por', 'con', 'una', 'bienvenido', 'gracias', 'hola', 'empresa', 'servicios', 'ayudar', 'está', 'cómo', 'puede', 'nuestro', 'nuestra', 'tiene', 'somos'];

  let scores = { pl: 0, en: 0, de: 0, fr: 0, es: 0 };

  // Polish diacritics are a very reliable indicator
  if (/[ąćęłńóśźż]/.test(lower)) scores.pl += 4;
  if (/[äöüß]/.test(lower)) scores.de += 4;
  if (/[áéíóúñ¿¡]/.test(lower)) scores.es += 4;
  if (/[âêîôûàèùëïç]/.test(lower)) scores.fr += 4;

  const words = lower.match(/\b[a-zżźćńółęąśäöüßáéíóúñâêîôûàèùëïç]+\b/g) || [];
  for (const w of words) {
    if (plWords.includes(w)) scores.pl++;
    if (enWords.includes(w)) scores.en++;
    if (deWords.includes(w)) scores.de++;
    if (frWords.includes(w)) scores.fr++;
    if (esWords.includes(w)) scores.es++;
  }

  let bestLangCode = fallbackLang ? fallbackLang.split('-')[0] : 'pl';
  let maxScore = 0;
  for (const [lang, score] of Object.entries(scores)) {
    if (score > maxScore && score >= 2) {
      maxScore = score;
      bestLangCode = lang;
    }
  }

  const langMap = {
    en: 'en-US',
    pl: 'pl-PL',
    de: 'de-DE',
    fr: 'fr-FR',
    es: 'es-ES'
  };

  return langMap[bestLangCode] || fallbackLang || 'pl-PL';
};

export const speakTextWithNativeVoice = (text, fallbackLang = 'pl-PL', onEndCallback = null) => {
  if (!('speechSynthesis' in window) || !text) {
    if (onEndCallback) onEndCallback();
    return;
  }

  try {
    window.speechSynthesis.cancel();
  } catch (e) {
    console.error('Failed to cancel previous speech:', e);
  }

  const targetLangFull = detectSpeechLanguage(text, fallbackLang);
  const targetLangPrefix = targetLangFull.split('-')[0].toLowerCase();

  const utterance = new SpeechSynthesisUtterance(text);
  if (onEndCallback && typeof onEndCallback === 'function') {
    utterance.onend = () => onEndCallback();
    utterance.onerror = () => onEndCallback();
  }
  utterance.lang = targetLangFull;
  utterance.rate = 1.0;
  utterance.pitch = 1.0;

  const assignBestVoiceAndSpeak = () => {
    const voices = window.speechSynthesis.getVoices();
    if (voices && voices.length > 0) {
      const matchingVoices = voices.filter(v => v.lang && v.lang.toLowerCase().startsWith(targetLangPrefix));
      if (matchingVoices.length > 0) {
        // Score voices to choose the cleanest, most natural native accent
        matchingVoices.sort((a, b) => {
          let scoreA = 0;
          let scoreB = 0;

          if (a.name.includes('Natural')) scoreA += 20;
          if (b.name.includes('Natural')) scoreB += 20;

          if (a.name.includes('Online')) scoreA += 15;
          if (b.name.includes('Online')) scoreB += 15;

          if (a.name.includes('Google')) scoreA += 12;
          if (b.name.includes('Google')) scoreB += 12;

          if (a.name.includes('Microsoft')) scoreA += 8;
          if (b.name.includes('Microsoft')) scoreB += 8;

          if (a.lang.toLowerCase().includes('us') || a.lang.toLowerCase().includes('gb') || a.lang.toLowerCase().includes('pl')) scoreA += 3;
          if (b.lang.toLowerCase().includes('us') || b.lang.toLowerCase().includes('gb') || b.lang.toLowerCase().includes('pl')) scoreB += 3;

          return scoreB - scoreA;
        });
        utterance.voice = matchingVoices[0];
        console.log(`🗣️ Selected native TTS voice: ${matchingVoices[0].name} (${matchingVoices[0].lang}) for text language: ${targetLangFull}`);
      } else {
        console.log(`🗣️ No exact voice match found for ${targetLangPrefix}, relying on default browser TTS for lang: ${targetLangFull}`);
      }
    }
    window.speechSynthesis.speak(utterance);
  };

  const voices = window.speechSynthesis.getVoices();
  if (voices && voices.length > 0) {
    assignBestVoiceAndSpeak();
  } else if (window.speechSynthesis.onvoiceschanged !== undefined) {
    window.speechSynthesis.onvoiceschanged = () => {
      assignBestVoiceAndSpeak();
      window.speechSynthesis.onvoiceschanged = null;
    };
    // Fallback timer if onvoiceschanged doesn't fire within 500ms
    setTimeout(() => {
      if (!window.speechSynthesis.speaking) {
        assignBestVoiceAndSpeak();
      }
    }, 500);
  } else {
    window.speechSynthesis.speak(utterance);
  }
};

export const stopAllSpeech = () => {
  if ('speechSynthesis' in window) {
    try {
      window.speechSynthesis.cancel();
    } catch (e) {
      console.error('Failed to cancel speech synthesis:', e);
    }
  }
  if (window.currentPlayingAudio) {
    try {
      window.currentPlayingAudio.pause();
      window.currentPlayingAudio.currentTime = 0;
    } catch (e) {
      console.error('Failed to stop audio player:', e);
    }
    window.currentPlayingAudio = null;
  }
};

export const speakAIResponse = async (textToSpeak, lang, onEnd) => {
  if (!textToSpeak) {
    if (onEnd) onEnd();
    return;
  }
  try {
    const res = await fetch('http://localhost:8020/tts_to_audio/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        text: String(textToSpeak).substring(0, 350),
        speaker_wav: "female",
        language: lang === 'pl' || String(lang).startsWith('pl') ? 'pl' : 'en'
      })
    });

    if (res.ok) {
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      window.currentPlayingAudio = audio;
      audio.onended = () => {
        if (onEnd) onEnd();
      };
      audio.play();
      return;
    } else {
      throw new Error("XTTS HTTP error " + res.status);
    }
  } catch (err) {
    console.warn("Local XTTS offline, falling back to synthesis:", err);
    speakTextWithNativeVoice(textToSpeak, lang, onEnd);
  }
};


